export class WebSocketManager {
    constructor(baseUrl) {
        this.ws = null;
        this.token = null;
        this.reconnectAttempts = 0;
        this.maxReconnectAttempts = 5;
        this.reconnectDelay = 1000; // Start with 1 second
        this.maxReconnectDelay = 30000; // Max 30 seconds
        this.reconnectTimer = null;
        this.pingInterval = null;
        this.messageHandlers = new Map();
        this.connectionListeners = new Set();
        this.isIntentionallyClosed = false;
        // Convert http(s) URL to ws(s)
        const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        const host = window.location.host;
        this.url = `${protocol}//${host}${baseUrl}`;
    }
    // Connect with JWT token
    connect(token) {
        var _a, _b;
        if (((_a = this.ws) === null || _a === void 0 ? void 0 : _a.readyState) === WebSocket.OPEN || ((_b = this.ws) === null || _b === void 0 ? void 0 : _b.readyState) === WebSocket.CONNECTING) {
            return;
        }
        this.token = token;
        this.isIntentionallyClosed = false;
        try {
            // Append token (and any configured parameters) as query parameters
            const wsUrl = `${this.url}?${new URLSearchParams(Object.assign(Object.assign({}, connectionParams), { token }))}`;
            this.ws = new WebSocket(wsUrl);
            this.ws.onopen = () => {
                this.reconnectAttempts = 0;
                this.reconnectDelay = 1000;
                this.notifyConnectionListeners(true);
                this.startPingInterval();
            };
            this.ws.onmessage = (event) => {
                try {
                    const message = JSON.parse(event.data);
                    this.handleMessage(message);
                }
                catch (error) {
                    console.error('[WebSocket] Failed to parse message:', error);
                }
            };
            this.ws.onerror = (error) => {
                console.error('[WebSocket] Error:', error);
            };
            this.ws.onclose = () => {
                this.stopPingInterval();
                this.notifyConnectionListeners(false);
                // Attempt reconnection unless intentionally closed
                if (!this.isIntentionallyClosed && this.token) {
                    this.scheduleReconnect();
                }
            };
        }
        catch (error) {
            console.error('[WebSocket] Connection failed:', error);
            this.scheduleReconnect();
        }
    }
    // Disconnect
    disconnect() {
        this.isIntentionallyClosed = true;
        this.clearReconnectTimer();
        this.stopPingInterval();
        if (this.ws) {
            if (this.ws.readyState === WebSocket.OPEN) {
                this.ws.close(1000, 'Client disconnect');
            }
            this.ws = null;
        }
        this.notifyConnectionListeners(false);
    }
    // Send message to server
    send(type, data) {
        var _a;
        if (((_a = this.ws) === null || _a === void 0 ? void 0 : _a.readyState) === WebSocket.OPEN) {
            const message = { type, data: data || {} };
            this.ws.send(JSON.stringify(message));
        }
        else {
            console.warn('[WebSocket] Cannot send message - not connected');
        }
    }
    // Subscribe to specific message type
    on(messageType, handler) {
        if (!this.messageHandlers.has(messageType)) {
            this.messageHandlers.set(messageType, new Set());
        }
        this.messageHandlers.get(messageType).add(handler);
        // Return unsubscribe function
        return () => {
            const handlers = this.messageHandlers.get(messageType);
            if (handlers) {
                handlers.delete(handler);
                if (handlers.size === 0) {
                    this.messageHandlers.delete(messageType);
                }
            }
        };
    }
    // Listen to connection status changes
    onConnectionChange(listener) {
        this.connectionListeners.add(listener);
        // Immediately notify of current state
        listener(this.isConnected());
        // Return unsubscribe function
        return () => {
            this.connectionListeners.delete(listener);
        };
    }
    // Check if connected
    isConnected() {
        var _a;
        return ((_a = this.ws) === null || _a === void 0 ? void 0 : _a.readyState) === WebSocket.OPEN;
    }
    // Get connection state
    getState() {
        if (!this.ws)
            return 'closed';
        switch (this.ws.readyState) {
            case WebSocket.CONNECTING: return 'connecting';
            case WebSocket.OPEN: return 'open';
            case WebSocket.CLOSING: return 'closing';
            case WebSocket.CLOSED: return 'closed';
            default: return 'closed';
        }
    }
    // Private methods
    handleMessage(message) {
        // Handle system messages
        switch (message.type) {
            case 'connected':
            case 'pong':
                // Connection confirmed / pong received
                break;
            default: {
                // Dispatch to registered handlers
                const handlers = this.messageHandlers.get(message.type);
                if (handlers) {
                    handlers.forEach(handler => {
                        try {
                            handler(message);
                        }
                        catch (error) {
                            console.error(`[WebSocket] Handler error for ${message.type}:`, error);
                        }
                    });
                }
            }
        }
    }
    startPingInterval() {
        this.stopPingInterval();
        // Send ping every 30 seconds to keep connection alive
        this.pingInterval = setInterval(() => {
            this.send('ping', { timestamp: new Date().toISOString() });
        }, 30000);
    }
    stopPingInterval() {
        if (this.pingInterval) {
            clearInterval(this.pingInterval);
            this.pingInterval = null;
        }
    }
    scheduleReconnect() {
        if (this.reconnectAttempts >= this.maxReconnectAttempts) {
            return;
        }
        this.clearReconnectTimer();
        const delay = Math.min(this.reconnectDelay * Math.pow(2, this.reconnectAttempts), this.maxReconnectDelay);
        this.reconnectTimer = setTimeout(() => {
            this.reconnectAttempts++;
            if (this.token) {
                this.connect(this.token);
            }
        }, delay);
    }
    clearReconnectTimer() {
        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
        }
    }
    notifyConnectionListeners(connected) {
        this.connectionListeners.forEach(listener => {
            try {
                listener(connected);
            }
            catch (error) {
                console.error('[WebSocket] Connection listener error:', error);
            }
        });
    }
}
// Global WebSocket instance (singleton per tenant)
let globalWebSocket = null;
// Extra query parameters sent on connect, for what a browser can't put in a WebSocket's headers
let connectionParams = {};
export const setWebSocketParams = (params) => {
    connectionParams = params;
};
export const getWebSocketManager = () => {
    if (!globalWebSocket) {
        globalWebSocket = new WebSocketManager('/api/ws');
    }
    return globalWebSocket;
};
export const resetWebSocketManager = () => {
    if (globalWebSocket) {
        globalWebSocket.disconnect();
        globalWebSocket = null;
    }
};
